import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock as LockIcon, AlertCircle, Eye, EyeOff, Heart, ArrowRight } from 'lucide-react';

const AuthPage = () => {
  const { login, signup, signInWithGoogle } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── Styles ─────────────────────────────────────────────────────────────────
  const containerStyle = {
    minHeight: '100vh',
    width: '100%',
    background: 'var(--background)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 16px',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: '420px',
    background: 'var(--card)',
    borderRadius: '28px',
    border: '1px solid var(--border)',
    boxShadow: '0 8px 32px rgba(42,40,36,0.08)',
    padding: '40px 32px 36px',
    animation: 'fadeIn 0.4s ease-out',
  };

  const logoContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '8px',
  };

  const logoBoxStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'var(--foreground)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const inputGroupStyle = {
    position: 'relative',
    marginBottom: '16px',
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--foreground)',
    marginBottom: '8px',
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    paddingLeft: '44px',
    borderRadius: '14px',
    border: '1.5px solid var(--border)',
    background: 'var(--background)',
    fontSize: '1rem',
    color: 'var(--foreground)',
    transition: 'all 0.2s ease',
    outline: 'none',
  };

  const iconStyle = {
    position: 'absolute',
    left: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--muted-foreground)',
  };

  const primaryBtnStyle = {
    width: '100%',
    padding: '15px 24px',
    borderRadius: '14px',
    border: 'none',
    background: 'var(--foreground)',
    color: 'var(--primary-foreground)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 14px rgba(42, 40, 36, 0.12)',
  };

  const googleBtnStyle = {
    width: '100%',
    padding: '14px 24px',
    borderRadius: '14px',
    border: '1.5px solid var(--border)',
    background: 'var(--card)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    color: 'var(--foreground)',
    transition: 'all 0.2s ease',
  };

  const dividerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    margin: '24px 0',
    color: 'var(--muted-foreground)',
    fontSize: '0.85rem',
  };

  const dividerLineStyle = {
    flex: 1,
    height: '1px',
    background: 'var(--border)',
  };

  return (
    <div style={containerStyle}>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .auth-input:focus {
          border-color: var(--foreground) !important;
          box-shadow: 0 0 0 3px rgba(59, 56, 51, 0.08) !important;
        }
        .auth-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(42, 40, 36, 0.18);
        }
        .google-btn:hover:not(:disabled) {
          background: var(--background);
          border-color: var(--muted-foreground);
        }
      `}</style>

      <div style={cardStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={logoContainerStyle}>
            <div style={logoBoxStyle}>
              <Heart size={24} color="var(--primary-foreground)" fill="var(--primary-foreground)" />
            </div>
          </div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: 'var(--foreground)',
            marginBottom: '8px',
            letterSpacing: '-0.02em',
          }}>
            HairRabbit
          </h1>
          <p style={{
            color: 'var(--muted-foreground)',
            fontSize: '0.95rem',
            margin: 0,
          }}>
            {isLogin ? 'Welcome back! Sign in to continue.' : 'Create your account to get started.'}
          </p>
        </div>

        {/* Disclaimer */}
        <div style={{
          background: 'var(--background)',
          border: '1px solid var(--border)',
          borderRadius: '14px',
          padding: '14px 16px',
          marginBottom: '28px',
          fontSize: '0.8rem',
          color: 'var(--muted-foreground)',
          lineHeight: '1.55',
          display: 'flex',
          gap: '10px',
        }}>
          <span style={{ fontSize: '1rem' }}>ℹ️</span>
          <span><strong style={{ color: 'var(--foreground)' }}>For tracking only:</strong> This app does not provide medical diagnosis or treatment.</span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              color: '#DC2626',
              padding: '14px 16px',
              borderRadius: '14px',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              marginBottom: '20px',
            }}>
              <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
              <span>{error}</span>
            </div>
          )}

          {/* Email Field */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <Mail size={18} style={iconStyle} />
              <input
                type="email"
                className="auth-input"
                style={inputStyle}
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
          </div>

          {/* Password Field */}
          <div style={inputGroupStyle}>
            <label style={labelStyle}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <LockIcon size={18} style={iconStyle} />
              <input
                type={showPassword ? 'text' : 'password'}
                className="auth-input"
                style={{ ...inputStyle, paddingRight: '44px' }}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '14px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: 'var(--muted-foreground)',
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-btn"
            style={primaryBtnStyle}
            disabled={loading}
            data-testid="submit-button"
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            {!loading && <ArrowRight size={18} />}
          </button>

          {/* Divider */}
          <div style={dividerStyle}>
            <div style={dividerLineStyle} />
            <span>or continue with</span>
            <div style={dividerLineStyle} />
          </div>

          {/* Google Sign In */}
          <button
            type="button"
            className="google-btn"
            style={googleBtnStyle}
            onClick={handleGoogleSignIn}
            disabled={loading}
            data-testid="google-signin-button"
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9.003 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707 0-.593.102-1.17.282-1.709V4.958H.957C.347 6.173 0 7.548 0 9c0 1.452.348 2.827.957 4.042l3.007-2.335z"/>
              <path fill="#EA4335" d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"/>
            </svg>
            Sign in with Google
          </button>
        </form>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '28px',
          color: 'var(--muted-foreground)',
          fontSize: '0.95rem',
        }}>
          {isLogin ? (
            <>
              Don't have an account?{' '}
              <button
                onClick={() => { setIsLogin(false); setError(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--foreground)',
                  cursor: 'pointer',
                  fontWeight: '600',
                  padding: 0,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
                data-testid="toggle-signup"
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                onClick={() => { setIsLogin(true); setError(''); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--foreground)',
                  cursor: 'pointer',
                  fontWeight: '600',
                  padding: 0,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
                data-testid="toggle-login"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;